/**
 * Public Programs page — highlights the 4 flagship Nodera programs at launch.
 *
 * Sleep & Recovery Optimization · Metabolic Health & Weight Management ·
 * Longevity & Performance Optimization · Precision Health Membership.
 *
 * These map 1:1 to the seeded `workflow_program_templates` in the new
 * Workflow Engine.
 */
import { Link } from "react-router-dom";
import { Section, FadeIn } from "@/components/Section";
import { ArrowRight, Moon, Flame, Dna, Repeat, CheckCircle2 } from "lucide-react";

const FLAGSHIPS = [
  {
    slug: "sleep-recovery",
    name: "Sleep & Recovery Optimization",
    icon: Moon,
    accent: "from-indigo-600 to-blue-500",
    pillBg: "bg-indigo-50 text-indigo-700 border-indigo-100",
    tagline: "Diagnostic-led sleep optimisation that turns nightly recovery into a measurable, billable line of care.",
    pillars: [
      "At-home sleep study + clinical interpretation",
      "Treatment plan delivered within days of results",
      "30-day follow-up baked in",
    ],
    sla: "From intake to plan in ~3 weeks",
  },
  {
    slug: "metabolic-weight",
    name: "Metabolic Health & Weight Management",
    icon: Flame,
    accent: "from-orange-500 to-rose-500",
    pillBg: "bg-orange-50 text-orange-700 border-orange-100",
    tagline: "Lab-driven metabolic care with built-in coaching and a structured 30 / 90-day follow-up cadence.",
    pillars: [
      "Comprehensive metabolic + hormone panel",
      "Provider-reviewed treatment plan",
      "Integrated health coaching from kickoff to renewal",
    ],
    sla: "First lab results in ~10 days",
  },
  {
    slug: "longevity-performance",
    name: "Longevity & Performance Optimization",
    icon: Dna,
    accent: "from-emerald-500 to-teal-500",
    pillBg: "bg-emerald-50 text-emerald-700 border-emerald-100",
    tagline: "Comprehensive longevity workup — biological age, VO₂max, advanced labs — into a personalised protocol.",
    pillars: [
      "Biological-age + body-composition testing",
      "Advanced lab panel reviewed by your providers",
      "Quarterly follow-up cadence built in",
    ],
    sla: "Full workup in ~4 weeks",
  },
  {
    slug: "precision-health-membership",
    name: "Precision Health Membership",
    icon: Repeat,
    accent: "from-violet-600 to-fuchsia-500",
    pillBg: "bg-violet-50 text-violet-700 border-violet-100",
    tagline: "Annual concierge membership that wraps everything else in a recurring, predictable revenue contract.",
    pillars: [
      "White-glove onboarding for every member",
      "Annual workup + quarterly check-ins",
      "Automated renewal outreach + retention loop",
    ],
    sla: "365-day membership cycle",
  },
];

export default function Programs() {
  return (
    <div data-testid="programs-page">
      <Section className="pt-28 pb-12">
        <div className="max-w-3xl">
          <div className="text-xs uppercase tracking-[0.22em] font-semibold text-blue-600 mb-4">
            Flagship programs
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-slate-900 leading-[1.02]">
            Four programs, ready to deploy.
          </h1>
          <p className="mt-5 text-lg text-slate-600 leading-relaxed">
            Nodera launches with four turnkey cash-pay programs — each one
            packaged end-to-end with assessments, diagnostics, vendor
            coordination, automated workflows, subscription infrastructure,
            and operational SOPs. Pick one, plug it in, and start.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/demo"
              data-testid="programs-book-demo-btn"
              className="inline-flex items-center gap-2 bg-slate-900 text-white rounded-full px-6 py-3.5 text-sm font-semibold hover:bg-slate-800 transition-colors"
            >
              Book a Demo <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/contact"
              data-testid="programs-contact-btn-top"
              className="inline-flex items-center gap-2 bg-white border border-slate-300 text-slate-800 rounded-full px-6 py-3.5 text-sm font-semibold hover:bg-slate-50 transition-colors"
            >
              Talk to our team
            </Link>
          </div>
        </div>
      </Section>

      {/* 4-card flagship grid */}
      <Section className="pt-2">
        <div className="grid md:grid-cols-2 gap-5" data-testid="flagship-grid">
          {FLAGSHIPS.map((p) => {
            const Icon = p.icon;
            return (
              <FadeIn key={p.slug}>
                <article
                  data-testid={`flagship-card-${p.slug}`}
                  className="group h-full rounded-3xl border border-slate-200 bg-white p-7 flex flex-col hover:border-slate-300 hover:shadow-lg transition-all"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${p.accent} text-white flex items-center justify-center shadow-sm`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <span className={`text-[10px] uppercase tracking-[0.16em] font-bold border rounded-full px-2.5 py-1 ${p.pillBg}`}>
                      Flagship
                    </span>
                  </div>
                  <h2 className="mt-5 font-display text-2xl font-bold text-slate-900 leading-tight">
                    {p.name}
                  </h2>
                  <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                    {p.tagline}
                  </p>
                  <ul className="mt-5 space-y-2 flex-1">
                    {p.pillars.map((pillar) => (
                      <li key={pillar} className="flex items-start gap-2 text-sm text-slate-700">
                        <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                        <span>{pillar}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-5 pt-5 border-t border-slate-100 flex items-center justify-between gap-2">
                    <div className="text-[11px] font-medium text-slate-500">{p.sla}</div>
                    <Link
                      to="/demo"
                      data-testid={`flagship-card-cta-${p.slug}`}
                      className="inline-flex items-center gap-1 text-sm font-semibold text-blue-700 hover:text-blue-900"
                    >
                      See it live <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                  </div>
                </article>
              </FadeIn>
            );
          })}
        </div>
      </Section>

      {/* What's included */}
      <Section className="bg-slate-50 border-t border-slate-200">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { title: "Assessments → diagnostics → plan", body: "Every program leads with a digital intake, routes into vetted diagnostics, and lands a provider-reviewed plan in the patient's hands." },
            { title: "Vendor coordination, automated", body: "Vetted diagnostic, device, and service vendors plug straight into the Nodera Workflow Engine. Hand-offs are observable, escalatable, and audited." },
            { title: "Payment orchestration, built-in", body: "One checkout for the patient — and the clinic and every participating vendor get paid through the same flow. No invoice juggling, no manual reconciliation." },
          ].map((card) => (
            <div
              key={card.title}
              data-testid={`programs-include-${card.title.toLowerCase().replace(/[^a-z]+/g, '-')}`}
              className="rounded-2xl bg-white border border-slate-200 p-6"
            >
              <div className="text-sm font-bold text-slate-900">{card.title}</div>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{card.body}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section>
        <div className="rounded-3xl border border-slate-200 bg-white p-8 text-center">
          <h3 className="font-display text-2xl font-bold text-slate-900">Need a custom program?</h3>
          <p className="mt-2 text-sm text-slate-600 max-w-xl mx-auto">
            We extend the catalog based on clinic demand and vendor viability. Tell us what you're operationalizing.
          </p>
          <Link
            to="/contact"
            data-testid="programs-contact-btn"
            className="mt-5 inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-slate-800 transition-colors"
          >
            Talk to our team <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </Section>
    </div>
  );
}
